import { useCallback, useReducer } from 'react'

type ErrorResult = {
  error: Error
  message: string
}

type MutationResult<T = unknown> = ErrorResult | T
type UseMutationProps<MutationResponse, MutationArguments extends unknown[]> = {
  mutateMethod: (...args: MutationArguments) => Promise<MutationResponse>
  onSuccess?: (response: MutationResponse) => void
  onError?: (error: ErrorResult) => void
}

type MutationState<T = unknown> = {
  data: T | null
  error: string | null
  isLoading: boolean
  isCompleted: boolean
}

export const useMutation = <
  MutationResponse = unknown,
  MutationArguments extends unknown[] = unknown[]
>(
  props: UseMutationProps<MutationResponse, MutationArguments>
) => {
  const { mutateMethod, onSuccess = undefined, onError = undefined } = props
  const [state, dispatch] = useReducer(queryReducer, {
    isLoading: false,
    isCompleted: false,
    data: undefined,
    error: null,
  })
  const mutate = useCallback(
    async (...args: MutationArguments): Promise<MutationResponse> => {
      dispatch({ type: 'mutation-started' })
      const res = await mutateMethod(...args)
      if (isErrorResult(res)) {
        dispatch({ type: 'mutation-error', payload: res.message })
        if (onError) onError(res)
      } else {
        dispatch({ type: 'mutation-success', payload: res })
        if (onSuccess) onSuccess(res)
      }
      return res
    },
    [mutateMethod]
  )
  const typedReturn = [mutate, state] as [
    typeof mutate,
    MutationState<MutationResponse>
  ]
  return typedReturn
}

type MutationReducerAction<T = unknown> =
  | { type: 'mutation-started' }
  | { type: 'mutation-success'; payload: T }
  | { type: 'mutation-error'; payload: string }

function queryReducer<T = unknown>(
  state: MutationState<T>,
  action: MutationReducerAction
) {
  switch (action.type) {
    case 'mutation-started':
      return { ...state, isLoading: true }
    case 'mutation-success':
      return {
        ...state,
        isLoading: false,
        isCompleted: true,
        data: action.payload,
      }
    case 'mutation-error':
      return {
        ...state,
        isLoading: false,
        isCompleted: true,
        error: action.payload,
      }
    default:
      return state
  }
}

const isErrorResult = (value: unknown): value is ErrorResult => {
  return typeof value === 'object' && 'error' in value
}
